import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GraduationCap, AlertCircle, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { authService } from '../../services/api';

const ResetPasswordPage = () => {
  const { dark } = useTheme();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token');

  const [form, setForm] = useState({ newPassword: '', confirm: '' });
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setError('Invalid or missing reset token. Please request a new password reset link.');
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!token) return;
    if (form.newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (form.newPassword !== form.confirm) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await authService.resetPasswordByToken(token, form.newPassword);
      setDone(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '9px 40px 9px 12px', borderRadius: 8, boxSizing: 'border-box',
    border: `1px solid ${dark ? '#475569' : '#D1D5DB'}`,
    background: dark ? '#0F172A' : '#F8FAFC',
    color: dark ? '#F1F5F9' : '#0F172A', fontSize: 14, outline: 'none',
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: dark ? '#0F172A' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif',
      padding: 16,
    }}>
      <div style={{
        width: '100%', maxWidth: 420,
        background: dark ? '#1E293B' : '#fff',
        borderRadius: 20, padding: 40,
        boxShadow: '0 25px 60px rgba(0,0,0,0.3)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{
            width: 56, height: 56, borderRadius: 14,
            background: 'linear-gradient(135deg,#3B82F6,#8B5CF6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <GraduationCap size={28} color="#fff" />
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: dark ? '#F1F5F9' : '#0F172A', margin: '0 0 4px' }}>
            Reset Password
          </h1>
          <p style={{ fontSize: 13, color: dark ? '#64748B' : '#94A3B8', margin: 0 }}>
            Set a new password for your account
          </p>
        </div>

        {done ? (
          <div style={{ textAlign: 'center' }}>
            <CheckCircle size={48} color="#10B981" style={{ margin: '0 auto 16px', display: 'block' }} />
            <p style={{ fontSize: 15, fontWeight: 600, color: dark ? '#F1F5F9' : '#0F172A', marginBottom: 8 }}>
              Password reset successfully!
            </p>
            <p style={{ fontSize: 13, color: dark ? '#64748B' : '#94A3B8', marginBottom: 20 }}>
              Redirecting you to the login page…
            </p>
            <button
              onClick={() => navigate('/login')}
              style={{
                padding: '10px 28px', borderRadius: 8,
                background: 'linear-gradient(135deg,#3B82F6,#8B5CF6)',
                color: '#fff', border: 'none', fontWeight: 600, fontSize: 14, cursor: 'pointer',
              }}
            >
              Go to Login
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && (
              <div style={{
                padding: '8px 14px', borderRadius: 8, background: '#FEF2F2',
                color: '#EF4444', fontSize: 13, marginBottom: 16,
                display: 'flex', alignItems: 'flex-start', gap: 6,
              }}>
                <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} />{error}
              </div>
            )}

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: dark ? '#94A3B8' : '#64748B', marginBottom: 5 }}>
                New Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPwd ? 'text' : 'password'}
                  value={form.newPassword}
                  onChange={e => setForm({ ...form, newPassword: e.target.value })}
                  placeholder="Minimum 8 characters"
                  style={inputStyle}
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(p => !p)}
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: dark ? '#64748B' : '#94A3B8' }}
                >
                  {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: dark ? '#94A3B8' : '#64748B', marginBottom: 5 }}>
                Confirm New Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showConfirm ? 'text' : 'password'}
                  value={form.confirm}
                  onChange={e => setForm({ ...form, confirm: e.target.value })}
                  placeholder="Repeat your new password"
                  style={inputStyle}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(p => !p)}
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: dark ? '#64748B' : '#94A3B8' }}
                >
                  {showConfirm ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            <button
              type="submit" disabled={loading || !token}
              style={{
                width: '100%', padding: '12px', borderRadius: 10,
                background: (loading || !token) ? '#94A3B8' : 'linear-gradient(135deg,#3B82F6,#8B5CF6)',
                color: '#fff', border: 'none', fontSize: 15, fontWeight: 600,
                cursor: (loading || !token) ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? 'Resetting…' : 'Reset Password'}
            </button>

            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <button
                type="button"
                onClick={() => navigate('/login')}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#3B82F6', fontWeight: 500 }}
              >
                Back to Sign In
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
